import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useApolloClient } from "@apollo/client"
import AsyncSelect from "react-select/async"

const DEFAULT_PAGE_SIZE = 30
const SEARCH_DEBOUNCE_MS = 300

const dedupeOptions = (options = []) => {
    const seen = new Set()
    return options.filter((option) => {
        if (!option || option.value === undefined || option.value === null) return false
        if (seen.has(option.value)) return false
        seen.add(option.value)
        return true
    })
}

/**
 * Searchable single-select backed by a relay connection query.
 *
 * Every keystroke is debounced and sent to the server as `searchVariable`, so
 * the list is never limited to whatever happened to fit in the first page.
 */
function ConnectionSelect({
    query,
    variables = {},
    searchVariable = "searchTerm",
    getConnection,
    getOption,
    pageSize = DEFAULT_PAGE_SIZE,
    value = null,
    onChange,
    placeholder = "Select...",
    isDisabled = false,
    isClearable = true,
}) {
    const apolloClient = useApolloClient()
    const [defaultOptions, setDefaultOptions] = useState([])
    const [selectedOption, setSelectedOption] = useState(null)
    const debounceRef = useRef(null)

    // Callers build `variables` inline, so a fresh object arrives on every
    // render. Compare by content instead of identity or we refetch forever.
    const variablesKey = JSON.stringify(variables)

    const fetchOptions = useCallback(async (searchTerm) => {
        try {
            const { data } = await apolloClient.query({
                query,
                variables: {
                    ...JSON.parse(variablesKey),
                    first: pageSize,
                    [searchVariable]: searchTerm || null,
                },
                // The whole point of this component is reaching records the
                // cached first page never contained.
                fetchPolicy: "network-only",
            })
            const edges = (getConnection(data) || {}).edges || []
            return dedupeOptions(edges.map(({ node }) => getOption(node)))
        } catch (err) {
            return []
        }
    }, [apolloClient, getConnection, getOption, pageSize, query, searchVariable, variablesKey])

    useEffect(() => {
        // A disabled select is waiting on another field (sub-charge type waits
        // on charge type). Prefetching now would spend a request on an
        // unscoped list the user can't open anyway.
        if (isDisabled) return undefined
        let isMounted = true
        fetchOptions("").then((options) => {
            if (isMounted) setDefaultOptions(options)
        })
        return () => { isMounted = false }
    }, [fetchOptions, isDisabled])

    useEffect(() => () => clearTimeout(debounceRef.current), [])

    const knownOptions = useMemo(
        () => dedupeOptions([selectedOption, ...defaultOptions]),
        [defaultOptions, selectedOption]
    )

    const currentValue = useMemo(
        () => knownOptions.find((option) => option.value === value) || null,
        [knownOptions, value]
    )

    const loadOptions = useCallback((inputValue) => new Promise((resolve) => {
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => { fetchOptions(inputValue).then(resolve) }, SEARCH_DEBOUNCE_MS)
    }), [fetchOptions])

    return (
        <AsyncSelect
            className="bos-custom-select"
            classNamePrefix="bos-select"
            cacheOptions
            defaultOptions={knownOptions}
            loadOptions={loadOptions}
            value={currentValue}
            onChange={(option) => {
                setSelectedOption(option || null)
                onChange(option ? option.value : null, option || null)
            }}
            placeholder={placeholder}
            isDisabled={isDisabled}
            isClearable={isClearable}
        />
    )
}

export default ConnectionSelect
